import React, { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Loader2, Clock } from 'lucide-react';
import { MEDICAL_SPECIALISTS } from '../utils/specialists';
import countryList from 'react-select-country-list';

const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function DoctorForm({ initialData = null, onSubmit, onCancel, loading = false }) {
  const countries = useMemo(() => countryList().getData(), []);
  const [countrySearch, setCountrySearch] = useState('');
  const [selectedDays, setSelectedDays] = useState([]);
  const [showCustomSpecialist, setShowCustomSpecialist] = useState(false);
  const [customSpecialist, setCustomSpecialist] = useState('');
  const [specialistSearch, setSpecialistSearch] = useState('');
  const [showSpecialistDropdown, setShowSpecialistDropdown] = useState(false);
  const [clinicOpeningTime, setClinicOpeningTime] = useState('09:00');
  const [clinicClosingTime, setClinicClosingTime] = useState('17:00');
  const [slotDuration, setSlotDuration] = useState(15);
  const [timeSlots, setTimeSlots] = useState([]);

  const [formData, setFormData] = useState({
    professional_type: 'doctor',
    title: 'Dr.',
    first_name: '',
    middle_name: '',
    last_name: '',
    nmc_id: '',
    mobile_number: '',
    email: '',
    education: '',
    certification: '',
    mbbs_completion_date: '',
    country_of_mbbs: '',
    department: '',
    specialist: '',
    medical_concern: '',
    opd_charge_nepali: '',
    opd_charge_international: '',
    online_charge_nepali: '',
    online_charge_international: '',
    consultation_timings: {},
    is_verified: false,
    available_online: false,
    availability_mode: 'both'
  });

  // Convert 12-hour format (e.g., "09:00 AM") to 24-hour format (e.g., "09:00")
  const convertTo24Hour = (time12h) => {
    if (!time12h) return null;
    const [time, modifier] = time12h.split(' ');
    let [hours, minutes] = time.split(':');
    if (hours === '12') {
      hours = modifier === 'AM' ? '00' : '12';
    } else if (modifier === 'PM') {
      hours = parseInt(hours, 10) + 12;
    }
    return `${hours.toString().padStart(2, '0')}:${minutes}`;
  };

  // Generate time slots based on opening/closing time and slot duration
  const generateTimeSlots = (openTime, closeTime, duration) => {
    const slots = [];
    if (!openTime || !closeTime || !duration) return slots;

    const [openHour, openMin] = openTime.split(':').map(Number);
    const [closeHour, closeMin] = closeTime.split(':').map(Number);
    
    let currentMinutes = openHour * 60 + openMin;
    const endMinutes = closeHour * 60 + closeMin;
    
    while (currentMinutes <= endMinutes) {
      const hours = Math.floor(currentMinutes / 60);
      const mins = currentMinutes % 60;
      const timeStr = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
      slots.push(timeStr);
      currentMinutes += duration;
    }
    
    return slots;
  };

  // Fetch clinic settings for opening/closing times
  useEffect(() => {
    const fetchClinicSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/appointments/settings`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });
        if (response.ok) {
          const settings = await response.json();
          console.log('Clinic settings loaded:', settings);
          
          // Get slot duration (default 15 minutes)
          const duration = settings.appointment_slot_duration || settings.slot_duration_minutes || 15;
          setSlotDuration(duration);
          
          // Convert 12-hour format to 24-hour format for HTML5 time input
          if (settings.clinic_opening_time) {
            const openTime = convertTo24Hour(settings.clinic_opening_time);
            console.log('Opening time:', settings.clinic_opening_time, '→', openTime);
            if (openTime) {
              setClinicOpeningTime(openTime);
              // Generate time slots
              const closeTime = settings.clinic_closing_time ? convertTo24Hour(settings.clinic_closing_time) : '17:00';
              const slots = generateTimeSlots(openTime, closeTime, duration);
              setTimeSlots(slots);
            }
          }
          if (settings.clinic_closing_time) {
            const closeTime = convertTo24Hour(settings.clinic_closing_time);
            console.log('Closing time:', settings.clinic_closing_time, '→', closeTime);
            if (closeTime) setClinicClosingTime(closeTime);
          }
        }
      } catch (error) {
        console.error('Failed to fetch clinic settings:', error);
      }
    };
    fetchClinicSettings();
  }, []);

  useEffect(() => {
    if (initialData) {
      const timings = initialData.consultation_timings || {};
      const days = Object.keys(timings).filter(day => DAYS.includes(day));
      setSelectedDays(days);
      
      // Check if specialist is custom (not in predefined list)
      if (initialData.specialist && !MEDICAL_SPECIALISTS.includes(initialData.specialist)) {
        setShowCustomSpecialist(true);
        setCustomSpecialist(initialData.specialist);
      }
      
      setFormData({
        professional_type: initialData.professional_type || 'doctor',
        title: initialData.title,
        first_name: initialData.first_name,
        middle_name: initialData.middle_name || '',
        last_name: initialData.last_name,
        nmc_id: initialData.nmc_id || '',
        mobile_number: initialData.mobile_number || '',
        email: initialData.email || '',
        gender: initialData.gender || '',
        education: initialData.education || '',
        certification: initialData.certification || '',
        mbbs_completion_date: initialData.mbbs_completion_date || '',
        country_of_mbbs: initialData.country_of_mbbs || '',
        department: initialData.department || '',
        specialist: initialData.specialist || '',
        medical_concern: initialData.medical_concern || '',
        opd_charge_nepali: initialData.opd_charge_nepali || '',
        opd_charge_international: initialData.opd_charge_international || '',
        online_charge_nepali: initialData.online_charge_nepali || '',
        online_charge_international: initialData.online_charge_international || '',
        consultation_timings: timings,
        is_verified: initialData.is_verified || false,
        available_online: initialData.available_online || false,
        availability_mode: initialData.availability_mode || 'both'
      });
    }
  }, [initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Use custom specialist if selected
    const finalData = {
      ...formData,
      specialist: showCustomSpecialist ? customSpecialist : formData.specialist
    };
    
    onSubmit(finalData);
  };

  const handleDayToggle = (day) => {
    if (selectedDays.includes(day)) {
      setSelectedDays(selectedDays.filter(d => d !== day));
      const newTimings = { ...formData.consultation_timings };
      delete newTimings[day];
      setFormData({ ...formData, consultation_timings: newTimings });
    } else {
      setSelectedDays([...selectedDays, day]);
      setFormData({
        ...formData,
        consultation_timings: {
          ...formData.consultation_timings,
          [day]: { morning: '', day: '', evening: '' }
        }
      });
    }
  };

  const handleTimingChange = (day, session, value) => {
    setFormData({
      ...formData,
      consultation_timings: {
        ...formData.consultation_timings,
        [day]: {
          ...formData.consultation_timings[day],
          [session]: value
        }
      }
    });
  };

  const convertToDateInput = (dateStr) => {
    if (!dateStr) return '';
    try {
      const parts = dateStr.split('/');
      if (parts.length === 3) return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
    } catch (e) { return ''; }
    return '';
  };

  const convertFromDateInput = (dateStr) => {
    if (!dateStr) return '';
    try {
      const parts = dateStr.split('-');
      if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    } catch (e) { return ''; }
    return '';
  };

  const filteredCountries = countries.filter(c => c.label.toLowerCase().includes(countrySearch.toLowerCase()));

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Professional Type */}
      <div>
        <Label>Professional Type *</Label>
        <div className="flex gap-4 mt-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="professional_type" value="doctor" checked={formData.professional_type === 'doctor'} onChange={(e) => setFormData({ ...formData, professional_type: e.target.value })} />
            <span>Medical Doctor (MBBS/MD/DM)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="professional_type" value="dietitian" checked={formData.professional_type === 'dietitian'} onChange={(e) => setFormData({ ...formData, professional_type: e.target.value })} />
            <span>Dietitian/Nutritionist</span>
          </label>
        </div>
      </div>

      {/* Name Fields */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Title *</Label>
          <Select value={formData.title} onValueChange={(val) => setFormData({ ...formData, title: val })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Dr.">Dr.</SelectItem>
              <SelectItem value="Prof Dr.">Prof Dr.</SelectItem>
              <SelectItem value="Prof.">Prof.</SelectItem>
              <SelectItem value="Mr.">Mr.</SelectItem>
              <SelectItem value="Ms.">Ms.</SelectItem>
              <SelectItem value="Mrs.">Mrs.</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>First Name *</Label>
          <Input value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} required />
        </div>
        <div>
          <Label>Middle Name</Label>
          <Input value={formData.middle_name} onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })} />
        </div>
      </div>
      
      <div>
        <Label>Last Name *</Label>
        <Input value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} required />
      </div>

      {/* NMC ID */}
      <div>
        <Label>{formData.professional_type === 'doctor' ? 'NMC/Association ID *' : 'Professional ID (auto-generated if empty)'}</Label>
        <Input value={formData.nmc_id} onChange={(e) => setFormData({ ...formData, nmc_id: e.target.value })} required={formData.professional_type === 'doctor'} placeholder={formData.professional_type === 'doctor' ? 'NMC registration number' : 'Leave empty for auto-generation'} />
        {formData.professional_type === 'doctor' && <p className="text-xs text-gray-500 mt-1">Required for medical doctors registered with Nepal Medical Council</p>}
      </div>

      {/* Contact */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Mobile Number</Label>
          <Input value={formData.mobile_number} onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })} />
        </div>
        <div>
          <Label>Email</Label>
          <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
        </div>
      </div>

      {/* Specialist Dropdown with Search */}
      <div>
        <Label>Specialist</Label>
        <div className="space-y-2">
          {!showCustomSpecialist ? (
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Input 
                  placeholder="Search specialist..." 
                  value={specialistSearch || formData.specialist}
                  onChange={(e) => {
                    setSpecialistSearch(e.target.value);
                    setShowSpecialistDropdown(true);
                  }}
                  onFocus={() => setShowSpecialistDropdown(true)}
                />
                {showSpecialistDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {MEDICAL_SPECIALISTS
                      .filter(spec => spec.toLowerCase().includes((specialistSearch || '').toLowerCase()))
                      .map(spec => (
                        <div
                          key={spec}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                          onClick={() => {
                            setFormData({ ...formData, specialist: spec });
                            setSpecialistSearch('');
                            setShowSpecialistDropdown(false);
                          }}
                        >
                          {spec}
                        </div>
                      ))}
                    {MEDICAL_SPECIALISTS.filter(spec => spec.toLowerCase().includes((specialistSearch || '').toLowerCase())).length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500">No specialists found</div>
                    )}
                  </div>
                )}
                {formData.specialist && !specialistSearch && !showSpecialistDropdown && (
                  <div className="mt-1 text-sm text-gray-600">
                    Selected: <strong>{formData.specialist}</strong>
                    <button 
                      type="button"
                      className="ml-2 text-xs text-red-600 hover:underline"
                      onClick={() => setFormData({ ...formData, specialist: '' })}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => setShowCustomSpecialist(true)}>+ Custom</Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Input placeholder="Enter custom specialist" value={customSpecialist} onChange={(e) => setCustomSpecialist(e.target.value)} className="flex-1" />
              <Button type="button" variant="outline" size="sm" onClick={() => { setShowCustomSpecialist(false); setCustomSpecialist(''); }}>Cancel</Button>
            </div>
          )}
        </div>
      </div>

      {/* Education & Certification */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Education</Label>
          <Select value={formData.education} onValueChange={(val) => setFormData({ ...formData, education: val })}>
            <SelectTrigger><SelectValue placeholder="Select education" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="MBBS">MBBS</SelectItem>
              <SelectItem value="MD">MD</SelectItem>
              <SelectItem value="DM">DM</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Certification</Label>
          <Input value={formData.certification} onChange={(e) => setFormData({ ...formData, certification: e.target.value })} />
        </div>
      </div>

      {/* MBBS Date & Country */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>MBBS Completion Date</Label>
          <Input type="date" value={convertToDateInput(formData.mbbs_completion_date)} onChange={(e) => setFormData({ ...formData, mbbs_completion_date: convertFromDateInput(e.target.value) })} />
        </div>
        <div>
          <Label>Country of MBBS</Label>
          <div className="relative">
            <Input placeholder="Search country..." value={countrySearch} onChange={(e) => setCountrySearch(e.target.value)} onFocus={() => setCountrySearch('')} />
            {countrySearch && filteredCountries.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                {filteredCountries.map((country) => (
                  <div key={country.value} className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm" onClick={() => { setFormData({ ...formData, country_of_mbbs: country.label }); setCountrySearch(''); }}>
                    {country.label}
                  </div>
                ))}
              </div>
            )}
            {formData.country_of_mbbs && !countrySearch && <div className="mt-1 text-sm text-gray-600">Selected: <strong>{formData.country_of_mbbs}</strong></div>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Department</Label>
          <Input value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} />
        </div>
        <div>
          <Label>Medical Concern</Label>
          <Input value={formData.medical_concern} onChange={(e) => setFormData({ ...formData, medical_concern: e.target.value })} placeholder="Areas of expertise" />
        </div>
      </div>

      {/* Consultation Timings */}
      <div className="border-t pt-4">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Consultation Timings
        </h3>
        {clinicOpeningTime && clinicClosingTime && (
          <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
            <strong>Clinic Hours:</strong> {clinicOpeningTime} - {clinicClosingTime} 
            <span className="ml-2">({slotDuration} min slots)</span>
          </div>
        )}
        <div className="space-y-3">
          <div className="grid grid-cols-4 gap-2">
            {DAYS.map((day, idx) => (
              <label key={day} className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={selectedDays.includes(day)} onCheckedChange={() => handleDayToggle(day)} />
                <span className="text-sm">{DAY_LABELS[idx]}</span>
              </label>
            ))}
          </div>
          {selectedDays.length > 0 && (
            <div className="space-y-3 mt-4">
              {selectedDays.map(day => (
                <div key={day} className="p-3 bg-gray-50 rounded">
                  <h4 className="font-medium text-sm mb-2 capitalize">{day}</h4>
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Morning From</Label>
                        <Input 
                          type="time" 
                          min={clinicOpeningTime}
                          max={clinicClosingTime}
                          value={formData.consultation_timings[day]?.morning_from || ''} 
                          onChange={(e) => handleTimingChange(day, 'morning_from', e.target.value)} 
                          className="text-sm" 
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Morning To</Label>
                        <Input 
                          type="time" 
                          min={clinicOpeningTime}
                          max={clinicClosingTime}
                          value={formData.consultation_timings[day]?.morning_to || ''} 
                          onChange={(e) => handleTimingChange(day, 'morning_to', e.target.value)} 
                          className="text-sm" 
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Evening From</Label>
                        <Input 
                          type="time" 
                          min={clinicOpeningTime}
                          max={clinicClosingTime}
                          value={formData.consultation_timings[day]?.evening_from || ''} 
                          onChange={(e) => handleTimingChange(day, 'evening_from', e.target.value)} 
                          className="text-sm" 
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Evening To</Label>
                        <Input 
                          type="time" 
                          min={clinicOpeningTime}
                          max={clinicClosingTime}
                          value={formData.consultation_timings[day]?.evening_to || ''} 
                          onChange={(e) => handleTimingChange(day, 'evening_to', e.target.value)} 
                          className="text-sm" 
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Consultation Charges */}
      <div className="border-t pt-4">
        <h3 className="font-semibold mb-3">Consultation Charges (NPR)</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>OPD - Nepali Patient</Label>
            <Input type="number" step="0.01" value={formData.opd_charge_nepali} onChange={(e) => setFormData({ ...formData, opd_charge_nepali: e.target.value })} placeholder="0.00" />
          </div>
          <div>
            <Label>OPD - International Patient</Label>
            <Input type="number" step="0.01" value={formData.opd_charge_international} onChange={(e) => setFormData({ ...formData, opd_charge_international: e.target.value })} placeholder="0.00" />
          </div>
          <div>
            <Label>Online - Nepali Patient</Label>
            <Input type="number" step="0.01" value={formData.online_charge_nepali} onChange={(e) => setFormData({ ...formData, online_charge_nepali: e.target.value })} placeholder="0.00" />
          </div>
          <div>
            <Label>Online - International Patient</Label>
            <Input type="number" step="0.01" value={formData.online_charge_international} onChange={(e) => setFormData({ ...formData, online_charge_international: e.target.value })} placeholder="0.00" />
          </div>
        </div>
      </div>

      <div className="flex gap-6">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={formData.is_verified} onChange={(e) => setFormData({ ...formData, is_verified: e.target.checked })} />
          <span className="text-sm">Verified</span>
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={formData.available_online} onChange={(e) => setFormData({ ...formData, available_online: e.target.checked })} />
          <span className="text-sm">Available for Online Consultation</span>
        </label>
      </div>
      
      <div className="flex gap-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>Cancel</Button>
        <Button type="submit" disabled={loading} style={{ backgroundColor: "#652D90" }}>
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {initialData ? 'Update' : 'Create'} Professional
        </Button>
      </div>
    </form>
  );
}
